import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase'; // Import your initialized auth instance
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faIdBadge, faKey, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Container, Row, Col, Card, Spinner, Alert, Breadcrumb, ListGroup } from 'react-bootstrap'; // Using react-bootstrap

function UserProfilePage() {
    const [userData, setUserData] = useState(null);
    const [userClaims, setUserClaims] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        setError(null);
        const currentUser = auth.currentUser;

        if (currentUser) {
            // Set basic user data immediately
            setUserData({
                email: currentUser.email,
                uid: currentUser.uid,
                emailVerified: currentUser.emailVerified,
                creationTime: currentUser.metadata.creationTime,
                lastSignInTime: currentUser.metadata.lastSignInTime,
            });

            // Fetch custom claims (force refresh to get latest)
            currentUser.getIdTokenResult(true)
                .then((idTokenResult) => {
                    setUserClaims(idTokenResult.claims);
                    setError(null); // Clear any previous error
                })
                .catch((claimError) => {
                    console.error("Error fetching user claims:", claimError);
                    setError("Could not load user permissions (claims). Please try refreshing.");
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            // No user logged in
            setError("No user is currently logged in.");
            setLoading(false);
            // Optional: Redirect to login after a delay
            // setTimeout(() => navigate('/login'), 2000);
        }
    }, [navigate]); // Added navigate to dependency array

    // Helper to format date/time from metadata
    const formatMetadataDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString();
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Render Loading State
    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading profile...</span>
                </Spinner>
                <p className="mt-2">Loading profile data...</p>
            </Container>
        );
    }

    // Render Error State
    if (error) {
        return (
            <Container className="mt-3">
                <Breadcrumb>
                    <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                    <Breadcrumb.Item active>Profile Error</Breadcrumb.Item>
                </Breadcrumb>
                <Alert variant="danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
                 {!auth.currentUser && <Link to="/login" className="btn btn-primary">Go to Login</Link>}
            </Container>
        );
    }

    // Render Profile Data
    return (
        <Container className="mt-3">
            <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item active>My Profile</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <Card className="shadow-sm">
                        <Card.Header as="h2" className="text-center">
                            <FontAwesomeIcon icon={faUser} className="me-2" /> Your Profile
                        </Card.Header>
                        <Card.Body>
                            {userData && (
                                <>
                                    <h4 className="mb-3">Account Information</h4>
                                    <ListGroup variant="flush">
                                        <ListGroup.Item><strong>Email:</strong> {userData.email}</ListGroup.Item>
                                        <ListGroup.Item><strong>User ID (UID):</strong> {userData.uid}</ListGroup.Item>
                                        <ListGroup.Item><strong>Email Verified:</strong> {userData.emailVerified ? 'Yes' : 'No'}</ListGroup.Item>
                                        <ListGroup.Item><strong>Account Created:</strong> {formatMetadataDate(userData.creationTime)}</ListGroup.Item>
                                        <ListGroup.Item><strong>Last Sign In:</strong> {formatMetadataDate(userData.lastSignInTime)}</ListGroup.Item>
                                    </ListGroup>
                                    <hr className="my-4" />
                                </>
                            )}
                        </Card.Body>
                         <Card.Footer className="text-end">
                         </Card.Footer>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default UserProfilePage;